import React, { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react'
import {
  DateRangePicker,
  FeaturePromptPopup,
  HorizontalTimeline,
  PageHeader,
  Pill,
  RadioInput,
  SourceStatsCard,
} from '@demandlocal/components'
import { useParams } from 'react-router'
import { useGetCustomerSourceByCustomerSourceId } from '../../../../api/dealership/customers/sources/useGetCustomerSourceByCustomerSourceId'
import { useGetCustomerSourceDetailsById } from '../../../../api/dealership/customers/sources/useGetCustomerSourceDetailsById'
import { ReactComponent as Calendar } from '../../../../images/icons/icon_calendar.svg'
import { ReactComponent as Source } from '../../../../images/icons/source.svg'
import { ReactComponent as Users } from '../../../../images/icons/users.svg'
import dayjs from 'dayjs'
import { useGetCustomerSourceChartById } from '../../../../api/dealership/customers/sources/useGetCustomerSourceChartById'
import { DateRange } from '../../../../lib/types/DateRange'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import { match } from 'ts-pattern'
import { FormProvider, useForm } from 'react-hook-form'
import LeadsInsights from '../../../../components/Sources/Insights/LeadsInsights'
import SalesInsights from '../../../../components/Sources/Insights/SalesInsights'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../../store/selectors/userSelector'
import { useGetCustomerSourceInsights } from '../../../../api/dealership/customers/sources/useGetCustomerSourceInsights'
import StackedBarChartV2 from '../../../../components/Charts/StackedBarChartV2'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'

const DatePickerSelect = ({
  datePickerType,
  setDatePickerType,
  onDateRangeChange,
}: {
  datePickerType: string
  setDatePickerType: Dispatch<SetStateAction<string>>
  onDateRangeChange: (dateRange: DateRange) => void
}): JSX.Element => {
  const formContext = useForm({
    defaultValues: {
      date_picker: datePickerType,
    },
  })

  return (
    <FormProvider {...formContext}>
      <div className="min-w-[200px]">
        <RadioInput
          name={'date_picker'}
          options={[
            {
              label: 'Last 6 months',
              value: '6month',
            },
            {
              label: 'Custom',
              value: 'custom',
            },
          ]}
          onChange={(type) => {
            if (type === '6month') {
              onDateRangeChange({
                dateRange: {
                  startDate: dayjs()
                    .subtract(6, 'month')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                  endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
                },
              })
            } else {
              onDateRangeChange({
                dateRange: {
                  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                  endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
                },
              })
            }
            setDatePickerType(type)
          }}
        />
      </div>
    </FormProvider>
  )
}

const CustomerSourceDetails = (): JSX.Element => {
  const { customer_source_id } = useParams()
  const currentUser = useSelector(currentUserSelector)
  const referenceRef = useRef<HTMLDivElement | null>(null)
  const { dateRange, setDateRange } = useDateRange()
  const [datePickerType, setDatePickerType] = useState<string>('6month')
  const { data: source } = useGetCustomerSourceByCustomerSourceId(
    Number(customer_source_id),
  )
  const { data: details, isLoading: isLoadingDetails } = useGetCustomerSourceDetailsById(
    Number(customer_source_id),
  )

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    },
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const {
    data: { chartData } = {},
    isLoading: isLoadingChartData,
    isPreviousData: isPreviousChartData,
  } = useGetCustomerSourceChartById({
    customer_source_id: Number(customer_source_id),
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
      groupBy: datePickerType === '6month' ? 'month' : 'day',
    },
  })

  const {
    data: { insights } = {},
    isLoading: isLoadingInsights,
    isPreviousData: isPreviousInsightsData,
  } = useGetCustomerSourceInsights({
    customer_source_id: Number(customer_source_id),
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const dynamicTiles = useMemo(() => {
    if (isLoadingDetails) return []

    const base = [
      {
        title: 'Total Unique Customers',
        content: details?.customerCount ?? 0,
      },
      {
        title: 'Oldest Customer',
        content: dayjs(details?.oldestAction).format('MMM DD, YYYY') ?? '',
      },
    ]
    switch (details?.saas_customer_source_type) {
      case 1:
        return [
          ...base,
          {
            title: 'Total Leads',
            content: details?.leadsCount ?? 0,
          },
        ]
      case 2:
        return [
          ...base,
          {
            title: 'Total Sales',
            content: details?.salesCount ?? 0,
          },
        ]
      case 3:
        return [
          ...base,
          {
            title: 'Total Services',
            content: details?.serviceCount ?? 0,
          },
        ]
      default:
        return base
    }
  }, [
    isLoadingDetails,
    details?.customerCount,
    details?.leadsCount,
    details?.oldestAction,
    details?.saas_customer_source_type,
    details?.salesCount,
    details?.serviceCount,
  ])

  const difference = useMemo(() => {
    const currentMonthValue = Number(
      chartData?.data?.find((d) => d.date === dayjs().format('MMM YYYY'))?.items ?? 0,
    )
    const previousMonthValue = Number(
      chartData?.data?.find(
        (d) => d.date === dayjs().subtract(1, 'month').format('MMM YYYY'),
      )?.items ?? 0,
    )

    return currentMonthValue - previousMonthValue
  }, [chartData?.data])

  const chartTotal = useMemo(
    () =>
      Number(
        chartData?.data?.reduce((acc, cur) => {
          return acc + Number(cur.items)
        }, 0) ?? 0,
      ),
    [chartData?.data],
  )

  return (
    <div className={'flex flex-1 flex-col h-full'}>
      <PageHeader
        cardsOptions={dynamicTiles}
        backRoute={'../sources/list'}
        title={source?.customer_source}
        rightSlot={() => (
          <div className="flex flex-row gap-2 items-center">
            <span className="font-bold text-base">Update Frequency:</span>
            <Pill type={source?.saas_data_provider_id === 3 ? 'success' : 'warning'}>
              {source?.saas_data_provider_id === 2
                ? 'One time'
                : source?.saas_data_provider_id === 3
                ? 'Automated'
                : ''}
            </Pill>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col py-10 px-10 max-w-[1920px] w-full'}>
        <div className="flex flex-row justify-between gap-5">
          <SourceStatsCard
            icon={<Calendar className="stroke-primary-persian-blue" />}
            title={'Initial Upload Date'}
            metric={dayjs(details?.initialDate).format('MMM DD, YYYY')}
          />
          <SourceStatsCard
            icon={<Source className="stroke-primary-persian-blue" />}
            title={'Initial Source Size'}
            metric={details?.initialCount?.toString() ?? ''}
          />
          <SourceStatsCard
            icon={<Users className="stroke-primary-persian-blue" />}
            title={'Current Source Size'}
            metric={details?.currentCount?.toString() ?? ''}
            delta={details?.currentDelta}
          />
        </div>
        <div className="flex flex-row justify-end items-center pt-8 pb-4">
          <div className="flex flex-row items-center gap-2">
            <DatePickerSelect
              datePickerType={datePickerType}
              setDatePickerType={setDatePickerType}
              onDateRangeChange={onDateRangeChange}
            />
            {datePickerType === 'custom' && (
              <DateRangePicker filters={filters} onChange={onDateRangeChange} />
            )}
          </div>
        </div>
        <div>
          <div
            key={`${chartData?.type}`}
            className={`min-h-[270px] border-2 rounded-xl shadow-xl `}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>
                {isLoadingDetails
                  ? ''
                  : chartData?.type === 'leads'
                  ? 'Leads'
                  : chartData?.type === 'sales'
                  ? 'Sales'
                  : chartData?.type === 'service'
                  ? 'Service Appointments'
                  : 'Customers'}
              </span>
              <div className="flex flex-row gap-2 items-center">
                <span>{chartTotal}</span>
                {datePickerType === '6month' && (
                  <Pill type={difference >= 0 ? 'success' : 'error'}>
                    <div className="px-1 flex flex-row gap-1">
                      <span>{difference}</span>
                      <span>vs prev month</span>
                    </div>
                  </Pill>
                )}
              </div>
            </div>

            {currentUser?.role_id === 1 && (
              <FeaturePromptPopup
                title={'Use the bars to navigate'}
                description={
                  <p>
                    Use the bars to navigate to the customers list. Click on a bar to
                    navigate to the month.
                  </p>
                }
                referenceRef={referenceRef}
                name={'quick-report-generator-button'}
              />
            )}
            <div className={'h-[270px] p-2'} ref={referenceRef}>
              {datePickerType === '6month' ? (
                <StackedBarChartV2
                  data={chartData?.data ?? []}
                  isLoading={isLoadingChartData}
                  isPreviousData={isPreviousChartData}
                  groupKey={'actionType'}
                  xKey={'date'}
                  yKey={'items'}
                  customOptions={{
                    legend: {
                      display: false,
                    },
                  }}
                />
              ) : (
                <MultiLineChart
                  data={chartData?.data ?? []}
                  gradient={[
                    'blue-to-purple',
                    'red-to-orange',
                    'green-to-cyan',
                    'blue-to-cyan',
                    'green-to-lemon-green',
                  ]}
                  borderColor="black"
                  groupKey={'actionType'}
                  labelKey={'actionType'}
                  yKey={'items'}
                  xKey="date"
                  isLoading={isLoadingChartData}
                  isPreviousData={isPreviousChartData}
                />
              )}
            </div>
          </div>
        </div>

        {chartData?.type === 'leads' ? (
          <LeadsInsights
            chartTotal={chartTotal}
            insights={insights}
            isLoading={isLoadingInsights}
            isPreviousData={isPreviousInsightsData}
          />
        ) : chartData?.type === 'sales' ? (
          <SalesInsights
            chartTotal={chartTotal}
            insights={insights}
            isLoading={isLoadingInsights}
            isPreviousData={isPreviousInsightsData}
          />
        ) : (
          <></>
        )}
        <span className="font-bold pt-10">Timeline</span>
        <div className="overflow-y-scroll mt-10">
          <HorizontalTimeline arrowDirection="left">
            {(details?.sourceTimeline ?? [])
              .filter((item) => item.type === 'sourceUpdate')
              .map((item) => {
                const data = match(item.type)
                  .with('sourceUpdate', () => ({
                    title: `Week of ${dayjs(item.date).format('MMM DD, YYYY')}`,
                    body: `Source updated with ${item.items} customers`,
                  }))
                  .with('matchBack', () => ({
                    title: `${dayjs(item.date).format('MMM DD, YYYY')}`,
                    body: `Deduplicated ${item.items} customers`,
                  }))
                  .exhaustive()
                return (
                  <HorizontalTimeline.Item
                    key={`${item.date}-${item.type}`}
                    title={data.title}>
                    <span className="text-sm font-bold px-3">{data.body}</span>
                  </HorizontalTimeline.Item>
                )
              })}
          </HorizontalTimeline>
        </div>
      </div>
    </div>
  )
}

export default CustomerSourceDetails
